import { getBudgetAmountInRange, hasBudgetAmountChangeBy } from '../utils/budgetAmounts';
import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { accountMatchesExternalId, isAkahuId, getLinkedAccountIds } from '../utils/categoryAccountUtils';

const DataContext = createContext();

const API_BASE_URL = `${process.env.REACT_APP_API_BASE || 'http://localhost:5000'}/api`;

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [accounts, setAccounts] = useState([]); // New state for accounts
  const [actions, setActions] = useState([]); // New state for actions
  const [goals, setGoals] = useState([]); // Goals: targets pinned to account balances
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use fetch with AbortController for cleanup
  const fetchWithTimeout = useCallback(async (url, options = {}, timeout = 30000) => {
    // If a signal is provided in options, we don't create our own controller or timeout.
    // The caller is responsible for aborting.
    const controller = options.signal ? null : new AbortController();
    const timeoutId = controller ? setTimeout(() => controller.abort(), timeout) : null;
    
    // Get token from localStorage
    const token = localStorage.getItem('token');
    const authHeader = token ? { 'Authorization': `Bearer ${token}` } : {};
    
    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          ...(options.headers || {}),
          ...authHeader,
        },
        signal: options.signal || (controller && controller.signal),
      });
      
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      return response;
    } catch (error) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      throw error;
    }
  }, []);

  // Budget CRUD with optimistic updates
  const fetchBudgets = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/budgets`);
      
      if (!res.ok) throw new Error('Failed to fetch budgets');
      
      const data = await res.json();
      
      // Ensure each budget has a type field, defaulting to 'Expense' if not present
      const budgetsWithType = data.map(budget => ({
        ...budget,
        type: budget.type || 'Expense'
      }));
      
      setBudgets(budgetsWithType);
      return budgetsWithType;
    } catch (err) {
      console.error('Error fetching budgets:', err);
      setError('Failed to load budgets');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithTimeout]);

  const fetchAccounts = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/accounts`);
      
      if (!res.ok) throw new Error('Failed to fetch accounts');
      
      const data = await res.json();
      setAccounts(data);
      return data;
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithTimeout]); 

  const fetchActions = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/actions`);
      
      if (!res.ok) throw new Error('Failed to fetch actions');
      
      const data = await res.json();
      setActions(data);
      return data;
    } catch (err) {
      console.error('Error fetching actions:', err);
      setError('Failed to load actions');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithTimeout]);

  const fetchGoals = useCallback(async () => {
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/goals`);
      if (!res.ok) throw new Error('Failed to fetch goals');
      const data = await res.json();
      setGoals(data);
      return data;
    } catch (err) {
      console.error('Error fetching goals:', err);
      setError('Failed to load goals');
      throw err;
    }
  }, [fetchWithTimeout]);

  const addAccount = useCallback(async (account) => {
    try {
      // Validate required fields
      if (!account.name || account.name.trim() === '') {
        throw new Error('Account name is required');
      }
      
      // Create a clean account object without an ID
      // Let the server generate the proper format ID (acc_...)
      const accountToCreate = {
        name: account.name,
        type: account.type || 'checking',
        balance: account.balance || 0,
        color: account.color || `#${Math.floor(Math.random()*16777215).toString(16)}`,
        isActive: account.isActive !== undefined ? account.isActive : true,
        order: account.order !== undefined ? account.order : accounts.length,
        // Bank (Akahu) account ids this account is linked to, when known at creation.
        linkedAccounts: Array.isArray(account.linkedAccounts) ? account.linkedAccounts : [],
        lastUpdated: new Date()
      };
      
      // For optimistic update, we need a temporary ID
      const tempId = `temp_${Date.now()}`;
      const optimisticAccount = { 
        ...accountToCreate,
        id: tempId 
      };
      
      // Add to UI immediately (optimistic update)
      setAccounts(prev => [...prev, optimisticAccount]);
      
      // Log the account being sent to the API for debugging
      console.log('Creating account:', JSON.stringify(accountToCreate));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(accountToCreate),
      });
      
      if (!res.ok) {
        // Attempt to get error details
        let errorMessage;
        try {
          const errorData = await res.json();
          errorMessage = errorData.message || `Server returned ${res.status}`;
        } catch (e) {
          errorMessage = `Server returned ${res.status}`;
        }
        
        // Remove the optimistic account from state
        setAccounts(prev => prev.filter(a => a.id !== tempId));
        throw new Error(`Failed to add account: ${errorMessage}`);
      }
      
      // Get the created account with server-generated ID
      const newAccount = await res.json();
      
      // Replace the temporary account with the real one
      setAccounts(prev => prev.map(a => 
        a.id === tempId ? newAccount : a
      ));
      
      return newAccount;
    } catch (err) {
      console.error('Error adding account:', err);
      throw err;
    }
  }, [fetchWithTimeout, accounts]);
  
  const updateAccount = useCallback(async (id, updatedAccount) => {
    // Store original accounts for potential rollback
    const originalAccounts = accounts;
    
    try {
      // Optimistic update
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, ...updatedAccount } : a));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/accounts/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedAccount),
      });
      
      if (!res.ok) {
        // Rollback on error
        setAccounts(originalAccounts);
        throw new Error('Failed to update account');
      }
      
      const updated = await res.json();
      return updated;
    } catch (err) {
      console.error('Error updating account:', err);
      throw err;
    }
  }, [accounts, fetchWithTimeout]);
  
  const deleteAccount = useCallback(async (id) => {
    // Store original for potential rollback
    const originalAccounts = accounts;
    
    try {
      // Optimistic update
      setAccounts(prev => prev.filter(a => a.id !== id));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/accounts/${id}`, { method: 'DELETE' });
      
      if (!res.ok) {
        // Rollback on error
        setAccounts(originalAccounts);
        throw new Error('Failed to delete account');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting account:', err);
      throw err;
    }
  }, [accounts, fetchWithTimeout]);
  
  const updateAccountBalance = useCallback(async (id, balance) => {
    // Store original for potential rollback
    const originalAccounts = accounts;
    
    try {
      // Optimistic update
      setAccounts(prev => prev.map(a => a.id === id ? { ...a, balance, lastUpdated: new Date() } : a));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/accounts/${id}/balance`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ balance }),
      });
      
      if (!res.ok) {
        // Rollback on error
        setAccounts(originalAccounts);
        throw new Error('Failed to update account balance');
      }
      
      const updated = await res.json();
      return updated;
    } catch (err) {
      console.error('Error updating account balance:', err);
      throw err;
    }
  }, [accounts, fetchWithTimeout]);

  const addBudget = useCallback(async (budget) => {
    try {
      // Optimistic update with tempId for identification
      const tempId = Date.now().toString();
      const optimisticBudget = { ...budget, id: budget.id || tempId };
      
      setBudgets(prev => [...prev, optimisticBudget]);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/budgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(budget),
      });
      
      if (!res.ok) {
        // Rollback on error
        setBudgets(prev => prev.filter(b => b.id !== optimisticBudget.id));
        throw new Error('Failed to add budget');
      }
      
      const newBudget = await res.json();
      
      // Replace optimistic entry with server response
      setBudgets(prev => prev.map(b => 
        b.id === optimisticBudget.id ? newBudget : b
      ));
      
      return newBudget;
    } catch (err) {
      console.error('Error adding budget:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateBudget = useCallback(async (id, updatedBudget) => {
    try {
      // Keep the original visible until the server returns the dated history.
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/budgets/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedBudget),
      });
      
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.error || 'Failed to update budget');
      }
      
      const updated = await res.json();
      setBudgets(prev => prev.map(b => b.id === id ? updated : b));
      return updated;
    } catch (err) {
      console.error('Error updating budget:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const deleteBudget = useCallback(async (id) => {
    // Store original for potential rollback
    const originalBudgets = budgets;
    
    try {
      // Optimistic update
      setBudgets(prev => prev.filter(b => b.id !== id));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/budgets/${id}`, { method: 'DELETE' });
      
      if (!res.ok) {
        // Rollback on error
        setBudgets(originalBudgets);
        throw new Error('Failed to delete budget');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting budget:', err);
      throw err;
    }
  }, [budgets, fetchWithTimeout]);

  // Delete all budgets for a category - MUST be defined before deleteCategory
  const deleteBudgetsByCategory = useCallback(async (categoryId) => {
    // Store original for potential rollback
    const originalBudgets = budgets;
    
    try {
      // Optimistic update
      setBudgets(prev => prev.filter(b => b.categoryId !== categoryId));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/budgets/category/${categoryId}`, { 
        method: 'DELETE' 
      });
      
      if (!res.ok) {
        // Rollback on error
        setBudgets(originalBudgets);
        throw new Error('Failed to delete category budgets');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting category budgets:', err);
      // We'll still delete the category even if we can't delete its budgets
      return false;
    }
  }, [budgets, fetchWithTimeout]);

  // Add action CRUD functions
  const addAction = useCallback(async (action) => {
    try {
      const tempId = Date.now().toString();
      const optimisticAction = { ...action, id: action.id || tempId };
      
      setActions(prev => [...prev, optimisticAction]);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/actions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action),
      });
      
      if (!res.ok) {
        setActions(prev => prev.filter(a => a.id !== optimisticAction.id));
        throw new Error('Failed to add action');
      }
      
      const newAction = await res.json();
      
      setActions(prev => prev.map(a => 
        a.id === optimisticAction.id ? newAction : a
      ));
      
      return newAction;
    } catch (err) {
      console.error('Error adding action:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateAction = useCallback(async (id, updatedAction) => {
    const originalActions = actions;
    
    try {
      setActions(prev => prev.map(a => a.id === id ? { ...a, ...updatedAction } : a));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/actions/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedAction),
      });
      
      if (!res.ok) {
        setActions(originalActions);
        throw new Error('Failed to update action');
      }
      
      const updated = await res.json();
      return updated;
    } catch (err) {
      console.error('Error updating action:', err);
      throw err;
    }
  }, [actions, fetchWithTimeout]);

  const deleteAction = useCallback(async (idOrAction) => {
    const originalActions = actions;
    
    try {
      const actionId = typeof idOrAction === 'string'
        ? idOrAction
        : (idOrAction?.id || idOrAction?._id);

      if (!actionId) {
        throw new Error('Action id is missing');
      }

      setActions(prev => prev.filter(a => (a.id || a._id) !== actionId));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/actions/${actionId}`, { method: 'DELETE' });
      
      if (!res.ok) {
        setActions(originalActions);
        throw new Error('Failed to delete action');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting action:', err);
      throw err;
    }
  }, [actions, fetchWithTimeout]);


  // Goal CRUD with optimistic updates (mirrors the action functions)
  const addGoal = useCallback(async (goal) => {
    try {
      const tempId = `temp_${Date.now()}`;
      const optimisticGoal = { ...goal, id: goal.id || tempId };
      setGoals(prev => [...prev, optimisticGoal]);

      const res = await fetchWithTimeout(`${API_BASE_URL}/goals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goal),
      });

      if (!res.ok) {
        setGoals(prev => prev.filter(g => g.id !== optimisticGoal.id));
        throw new Error('Failed to add goal');
      }

      const newGoal = await res.json();
      setGoals(prev => prev.map(g => (g.id === optimisticGoal.id ? newGoal : g)));
      return newGoal;
    } catch (err) {
      console.error('Error adding goal:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateGoal = useCallback(async (id, updatedGoal) => {
    const originalGoals = goals;
    try {
      setGoals(prev => prev.map(g => (g.id === id ? { ...g, ...updatedGoal } : g)));

      const res = await fetchWithTimeout(`${API_BASE_URL}/goals/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedGoal),
      });

      if (!res.ok) {
        setGoals(originalGoals);
        throw new Error('Failed to update goal');
      }

      return await res.json();
    } catch (err) {
      console.error('Error updating goal:', err);
      throw err;
    }
  }, [goals, fetchWithTimeout]);

  const deleteGoal = useCallback(async (id) => {
    const originalGoals = goals;
    try {
      setGoals(prev => prev.filter(g => g.id !== id));

      const res = await fetchWithTimeout(`${API_BASE_URL}/goals/${id}`, { method: 'DELETE' });

      if (!res.ok) {
        setGoals(originalGoals);
        throw new Error('Failed to delete goal');
      }

      return true;
    } catch (err) {
      console.error('Error deleting goal:', err);
      throw err;
    }
  }, [goals, fetchWithTimeout]);

  // Fetch initial data with improved error handling
  useEffect(() => {
    if (!user) return; // Don't fetch data if not authenticated
    const controller = new AbortController();
    
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const [txnRes, catRes, ruleRes, budgetRes, acctRes, actRes, goalRes] = await Promise.all([
          fetchWithTimeout(`${API_BASE_URL}/transactions`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/categories`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/rules`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/budgets`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/accounts`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/actions`, { signal: controller.signal }),
          fetchWithTimeout(`${API_BASE_URL}/goals`, { signal: controller.signal }),
        ]);

        if (!txnRes.ok) throw new Error('Failed to fetch transactions');
        if (!catRes.ok) throw new Error('Failed to fetch categories');
        if (!ruleRes.ok) throw new Error('Failed to fetch rules');
        // Budget fetch might fail if the endpoint doesn't exist yet
        // We'll handle it gracefully

        if (acctRes.ok) {
          const acctData = await acctRes.json();
          setAccounts(acctData);
        }

        if (actRes.ok) {
          const actionData = await actRes.json();
          setActions(actionData);
        }

        if (goalRes.ok) {
          const goalData = await goalRes.json();
          setGoals(goalData);
        }

        // This function safely compares categories by order property
        const sortCategoriesSafely = (categories) => {
          return [...categories].sort((a, b) => {
            // Protected categories at the top
            if (['income', 'expense'].includes(a.id)) return -1;
            if (['income', 'expense'].includes(b.id)) return 1;
            
            // Use order property if both have it
            if (a.order !== undefined && b.order !== undefined) {
              return a.order - b.order;
            }
            
            // If only one has order, prioritize it
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            
            // Fall back to name sorting
            return (a.name || '').localeCompare(b.name || '');
          });
        };

        // Then where you need sorted categories
        const orderedCategories = sortCategoriesSafely(categories);

        const [txnData, catData, ruleData] = await Promise.all([
          txnRes.json(),
          catRes.json(),
          ruleRes.json(),
        ]);

        if (!controller.signal.aborted) {
          setTransactions(txnData);
          setCategories(catData);
          setRules(ruleData);

          if (budgetRes.ok) {
            const budgetData = await budgetRes.json();
            
            // Ensure each budget has a type field, defaulting to 'Expense' if not present
            const budgetsWithType = budgetData.map(budget => ({
              ...budget,
              type: budget.type || 'Expense'
            }));
            
            setBudgets(budgetsWithType);
          }
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          console.error('Failed to load data:', err);
          setError(err.message);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    
    // Cleanup function to abort fetch requests when component unmounts
    return () => {
      controller.abort();
    };
  }, [fetchWithTimeout, user]);

  // Function to create account if it doesn't exist when importing transactions
  const getOrCreateAccount = useCallback(async (accountName) => {
    // Check if account already exists
    const existingAccount = accounts.find(a => a.name.toLowerCase() === accountName.toLowerCase());
    
    if (existingAccount) {
      return existingAccount;
    }
    
    // Create a new account with a unique ID
    const newAccount = {
      id: `acct_${Date.now()}`,
      name: accountName,
      type: 'checking', // Default type
      balance: 0,
      color: `#${Math.floor(Math.random()*16777215).toString(16)}`, // Random color
      isActive: true,
      order: accounts.length
    };
    
    try {
      const createdAccount = await addAccount(newAccount);
      return createdAccount;
    } catch (err) {
      console.error('Failed to create account automatically:', err);
      return null;
    }
  }, [accounts, addAccount]);

  // Transaction CRUD operations with useCallback for stability
  const addTransaction = useCallback(async (transaction) => {
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/transactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transaction),
      });
      
      if (!res.ok) throw new Error('Failed to add transaction');
      
      const newTxn = await res.json();
      setTransactions(prev => [...prev, newTxn]);
      return newTxn;
    } catch (err) {
      console.error('Error adding transaction:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateTransaction = useCallback(async (updatedTransaction) => {
    try {
      // Optimistic update
      setTransactions(prev => 
        prev.map(txn => txn._id === updatedTransaction._id ? updatedTransaction : txn)
      );
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/transactions/${updatedTransaction._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTransaction),
      });
      
      if (!res.ok) {
        // Revert optimistic update on error
        setTransactions(prev => 
          prev.map(txn => txn._id === updatedTransaction._id ? 
            prev.find(t => t._id === updatedTransaction._id) : txn)
        );
        throw new Error('Failed to update transaction');
      }
      
      const updated = await res.json();
      return updated;
    } catch (err) {
      console.error('Error updating transaction:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const deleteTransaction = useCallback(async (id) => {
    // Store current transactions for potential rollback
    const currentTransactions = transactions;
    
    try {
      // Optimistic update
      setTransactions(prev => prev.filter(txn => txn._id !== id));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/transactions/${id}`, { method: 'DELETE' });
      
      if (!res.ok) {
        // Rollback on error
        setTransactions(currentTransactions);
        throw new Error('Failed to delete transaction');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting transaction:', err);
      throw err;
    }
  }, [transactions, fetchWithTimeout]);

  // Bulk import with progress reporting
  const addMultipleTransactions = useCallback(async (newTransactions, onProgress = () => {}, accountsList = null, importMetadata = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15 * 60 * 1000);

    const formatted = [];
    // Use the provided accounts list if available, otherwise fallback to state.
    // Copy it so accounts created mid-loop can be registered here and reused by
    // later transactions — otherwise every transaction for a not-yet-existing
    // account would spawn its own duplicate.
    const effectiveAccounts = [...(accountsList || accounts)];
  
    // Start import session if metadata provided
    let sessionId = null;
    if (importMetadata && Object.keys(importMetadata).length > 0) {
      try {
        const sessionResponse = await fetchWithTimeout(`${API_BASE_URL}/transactions/import-session/start`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(importMetadata),
          signal: controller.signal
        });
        if (sessionResponse.ok) {
          const sessionData = await sessionResponse.json();
          sessionId = sessionData.sessionId;
        }
      } catch (err) {
        console.error('Error starting import session:', err);
      }
    }
    
    // Process transactions and create accounts as needed
    for (const txn of newTransactions) {
      // Resolve the transaction's account against existing accounts. A match can
      // be by internal id, by a linked external (Akahu) account id, or by name.
      const linkedMatch = txn.account
        ? effectiveAccounts.find(a => accountMatchesExternalId(a, txn.account))
        : null;
      if (linkedMatch) {
        // File the transaction under the local account's internal id.
        txn.account = linkedMatch.id;
      } else if (txn.account) {
        try {
          console.log('🔍 Checking account:', txn.account);
          console.log('Current accounts:', effectiveAccounts.map(a => ({ id: a.id, name: a.name })));

          // Find if there's an account with this name - try both exact and trimmed comparison
          const existingAccount = effectiveAccounts.find(a => {
            const matches = a.name === txn.account || 
                          a.name.trim() === txn.account.trim() ||
                          a.name.toLowerCase() === txn.account.toLowerCase() ||
                          a.name.toLowerCase().trim() === txn.account.toLowerCase().trim();
            if (matches) {
              console.log('✅ Found matching account:', a);
            }
            return matches;
          });
          
          if (existingAccount) {
            // If we found an account with this name, use its ID
            console.log('🔄 Using existing account ID:', existingAccount.id);
            txn.account = existingAccount.id;
          } else {
            // Only create a new account if we don't have one with this name
            console.log('➕ Creating new account for:', txn.account);
            const newAccount = {
              id: `acct_${Date.now()}`,
              name: txn.account,
              type: 'checking', // Default type
              color: `#${Math.floor(Math.random()*16777215).toString(16)}`, // Random color
              isActive: true,
              // If the source is a bank (Akahu) account id, capture the link so
              // future imports file transactions here and the UI shows it linked.
              linkedAccounts: isAkahuId(txn.account) ? [txn.account] : []
            };

            const createdAccount = await addAccount(newAccount);
            console.log('✨ Created new account:', createdAccount);
            // Register it so later transactions for the same account reuse it
            // instead of creating another duplicate.
            effectiveAccounts.push(createdAccount);
            txn.account = createdAccount.id;
          }
        } catch (err) {
          console.error('Error handling account during import:', err);
        }
      }
      
      formatted.push({
        id: txn.id,
        date: txn.date,
        amount: txn.amount,
        description: txn.description,
        account: txn.account,
        balance: txn.balance || 0,
        category: txn.category || 'uncategorized',
        isTransfer: txn.isTransfer || false,
        isPending: txn.isPending || false,
        notes: txn.notes || '',
        type: txn.type || '',
        merchant: txn.merchant || null,
        meta: txn.meta || {}
      });
    }

    const chunkSize = 100;
    const total = formatted.length;
    const results = { inserted: 0, updated: 0, failed: 0 };
    const savedTxns = [];

    try {
      for (let i = 0; i < total; i += chunkSize) {
        const chunk = formatted.slice(i, i + chunkSize);
        
        // Build query parameters for import metadata
        const params = new URLSearchParams();
        if (sessionId) params.append('sessionId', sessionId);
        
        const url = params.toString() 
          ? `${API_BASE_URL}/transactions/batch?${params.toString()}`
          : `${API_BASE_URL}/transactions/batch`;
        
        const response = await fetchWithTimeout(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(chunk),
          signal: controller.signal
        });
        
        if (!response.ok) {
          const errData = await response.json();
          throw new Error(errData.error || 'Failed to save batch');
        }
        
        const batchResult = await response.json();
        results.inserted += batchResult.inserted || 0;
        results.updated += batchResult.updated || 0;
        results.failed += batchResult.failed || 0;
        
        if (batchResult.transactions) savedTxns.push(...batchResult.transactions);
        // Pass live cumulative stats so callers can show new/updated counts as they happen
        onProgress(Math.min(i + chunk.length, total), total, { ...results });
      }
      clearTimeout(timeout);

      // Use functional update to avoid race conditions
      setTransactions(prev => {
        // Merge on the same identity the UI uses everywhere else (`_id || id`).
        // Two things can otherwise produce phantom duplicates that only a full
        // reload clears:
        //   1. The /batch route matches incoming rows by the client `id`, so
        //      several imported rows (a duplicated CSV line, or distinct rows
        //      that collide on the date+description+amount hash) can resolve to
        //      one stored document and come back in `savedTxns` more than once.
        //   2. A stale `prev` copy keyed only by its client `id` wouldn't be
        //      removed by a saved copy keyed by `_id`.
        // Dedupe `savedTxns` and drop any `prev` entry that matches a saved one
        // by either identifier before appending.
        const savedMongoIds = new Set(savedTxns.map(t => t._id).filter(Boolean));
        const savedClientIds = new Set(savedTxns.map(t => t.id).filter(Boolean));

        const dedupedSaved = [];
        const seen = new Set();
        for (const t of savedTxns) {
          const key = t._id || t.id;
          if (key && seen.has(key)) continue;
          if (key) seen.add(key);
          dedupedSaved.push(t);
        }

        const filtered = prev.filter(t =>
          !(t._id && savedMongoIds.has(t._id)) &&
          !(t.id && savedClientIds.has(t.id))
        );
        return [...filtered, ...dedupedSaved];
      });

      // Complete import session if one was created
      if (sessionId) {
        try {
          await fetchWithTimeout(`${API_BASE_URL}/transactions/import-session/${sessionId}/complete`, {
            method: 'POST',
            signal: controller.signal
          });
        } catch (err) {
          console.error('Error completing import session:', err);
          // Don't throw - the transactions were saved successfully
        }
      }

      return results;
    } catch (err) {
      clearTimeout(timeout);
      console.error('Error saving transactions:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  // silent: refresh data without toggling the global isLoading flag, so
  // pages that render null while loading don't blank out mid-refresh.
  const fetchTransactions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError(null);
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/transactions`);
      if (!res.ok) throw new Error('Failed to fetch transactions');
      const txnData = await res.json();
      setTransactions(txnData);
    } catch (err) {
      setError(err.message);
      console.error('Failed to fetch transactions:', err);
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, [fetchWithTimeout]);

  const importTransactionsFromCSV = useCallback(async (csvTxns, onProgress, accountsList) => {
    try {
      const r = await addMultipleTransactions(csvTxns, onProgress, accountsList);
      await fetchTransactions(); // Ensure state is in sync after import
      return r.inserted + r.updated;
    } catch (err) {
      console.error('Error importing CSV transactions:', err);
      throw err;
    }
  }, [addMultipleTransactions, fetchTransactions]);

  // One-time migration: the old import dialog "remembered" bank-account
  // mappings only in this browser's localStorage, which the server-side
  // auto-sync can't see (it caused duplicate auto-created accounts). Push any
  // Akahu-id-keyed mappings into the target account's linkedAccounts so the
  // link is durable server-side, then drop them from localStorage. Name-keyed
  // CSV mappings are left alone — the CSV dialog still uses those.
  const mappingMigrationRef = useRef(false);
  useEffect(() => {
    if (!user || mappingMigrationRef.current || accounts.length === 0) return;
    mappingMigrationRef.current = true;
    (async () => {
      let saved;
      try {
        saved = JSON.parse(localStorage.getItem('budgetClues_accountMappings') || '{}');
      } catch {
        return;
      }
      let changed = false;
      for (const [key, mapping] of Object.entries(saved)) {
        if (!isAkahuId(key) || !mapping?.targetAccountId) continue;
        const target = accounts.find(a => a.id === mapping.targetAccountId);
        if (target) {
          const links = getLinkedAccountIds(target);
          if (!links.includes(key)) {
            try {
              await updateAccount(target.id, { linkedAccounts: [...links, key] });
              console.log(`Migrated saved mapping ${key} → "${target.name}" to linkedAccounts`);
            } catch (err) {
              console.error('Failed to migrate saved account mapping:', err);
              continue; // keep the localStorage entry for a retry next load
            }
          }
        }
        // Migrated (or target gone) — either way the entry is done.
        delete saved[key];
        changed = true;
      }
      if (changed) {
        try {
          localStorage.setItem('budgetClues_accountMappings', JSON.stringify(saved));
        } catch { /* non-fatal */ }
      }
    })();
  }, [user, accounts, updateAccount]);

  // Bank transactions now import server-side (webhook-triggered auto-sync),
  // so data can change while the tab is hidden. When the user comes back to
  // the tab after a while, refresh quietly to pick those changes up.
  const lastFocusRefetchRef = useRef(Date.now());
  useEffect(() => {
    if (!user) return undefined;
    const REFRESH_AFTER_MS = 2 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFocusRefetchRef.current < REFRESH_AFTER_MS) return;
      lastFocusRefetchRef.current = Date.now();
      fetchTransactions({ silent: true });
      fetchAccounts();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [user, fetchTransactions, fetchAccounts]);

  // Category CRUD operations
  const addCategory = useCallback(async (category) => {
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(category),
      });
      
      if (!res.ok) throw new Error('Failed to add category');
      
      const newCat = await res.json();
      setCategories(prev => [...prev, newCat]);
      return newCat;
    } catch (err) {
      console.error('Error adding category:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateCategory = useCallback(async (updatedCategory) => {
    // Store original for potential rollback
    const originalCategories = categories;
    
    try {
      // Optimistic update
      setCategories(prev => 
        prev.map(c => c.id === (updatedCategory.originalId || updatedCategory.id) ? 
          {...c, ...updatedCategory} : c)
      );
      
      const res = await fetchWithTimeout(
        `${API_BASE_URL}/categories/${updatedCategory.originalId || updatedCategory.id}`, 
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedCategory),
        }
      );
      
      if (!res.ok) {
        // Rollback on error
        setCategories(originalCategories);
        throw new Error('Failed to update category');
      }
      
      const updated = await res.json();
      return updated;
    } catch (err) {
      console.error('Error updating category:', err);
      throw err;
    }
  }, [categories, fetchWithTimeout]);

  // Optimized deletion with multiple state updates in one batch
  const deleteCategory = useCallback(async (id) => {
    // Store originals for potential rollback
    const originalCategories = categories;
    const originalTransactions = transactions;
    const originalBudgets = budgets;
    
    try {
      // Optimistic updates (batched in one render)
      setCategories(prev => prev.filter(c => c.id !== id));
      setTransactions(prev =>
        prev.map(t => t.category === id ? { ...t, category: 'uncategorized' } : t)
      );
      setBudgets(prev => prev.filter(b => b.categoryId !== id));
      
      // First, try to delete any budgets for this category
      await deleteBudgetsByCategory(id);
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/categories/${id}`, { method: 'DELETE' });
      
      if (!res.ok) {
        // Rollback all changes on error
        setCategories(originalCategories);
        setTransactions(originalTransactions);
        setBudgets(originalBudgets);
        throw new Error('Failed to delete category');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting category:', err);
      throw err;
    }
  }, [categories, transactions, budgets, fetchWithTimeout, deleteBudgetsByCategory]);

  // Rule CRUD
  const addRule = useCallback(async (rule) => {
    try {
      const res = await fetchWithTimeout(`${API_BASE_URL}/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rule),
      });
      
      if (!res.ok) throw new Error('Failed to add rule');
      
      const newRule = await res.json();
      setRules(prev => [...prev, newRule]);
      return newRule;
    } catch (err) {
      console.error('Error adding rule:', err);
      throw err;
    }
  }, [fetchWithTimeout]);

  const updateRule = useCallback(async (id, updatedRule) => {
    // Store original for potential rollback
    const originalRules = rules;
    
    try {
      // Optimistic update
      setRules(prev => prev.map(r => r._id === id ? {...r, ...updatedRule} : r));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/rules/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedRule),
      });
      
      if (!res.ok) {
        // Rollback on error
        setRules(originalRules);
        throw new Error('Failed to update rule');
      }
      
      const up = await res.json();
      return up;
    } catch (err) {
      console.error('Error updating rule:', err);
      throw err;
    }
  }, [rules, fetchWithTimeout]);

  const deleteRule = useCallback(async (id) => {
    // Store original for potential rollback
    const originalRules = rules;
    
    try {
      // Optimistic update
      setRules(prev => prev.filter(r => r._id !== id));
      
      const res = await fetchWithTimeout(`${API_BASE_URL}/rules/${id}`, { method: 'DELETE' });
      
      if (!res.ok) {
        // Rollback on error
        setRules(originalRules);
        throw new Error('Failed to delete rule');
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting rule:', err);
      throw err;
    }
  }, [rules, fetchWithTimeout]);

  // Budget analytics with memoization to prevent unnecessary calculations
  const getBudgetStatus = useCallback((budget, startDate, endDate) => {
    try {
      // Validate dates
      const start = startDate ? new Date(startDate) : new Date(budget.startDate);
      const end = endDate ? new Date(endDate) : budget.endDate ? new Date(budget.endDate) : new Date();
      
      // Determine if this is an income or expense budget
      const isIncomeBudget = budget.type === 'Income';
      
      // Get applicable transactions
      const relevantTransactions = transactions.filter(transaction => {
        // For expense budgets, consider negative amounts; for income budgets, consider positive amounts
        const matchesType = isIncomeBudget 
          ? transaction.amount > 0 
          : transaction.amount < 0;
        const matchesCategory = transaction.category === budget.categoryId;
        
        // Check if the transaction date falls within the budget period
        const txnDate = new Date(transaction.date);
        const isInDateRange = txnDate >= start && txnDate <= end;
        
        return matchesType && matchesCategory && isInDateRange;
      });
      
      // Calculate total spent/earned
      const spent = relevantTransactions.reduce((total, txn) => total + Math.abs(txn.amount), 0);
      
      // Calculate budget amount (sum for each full period in range)
      let adjustedBudget = budget.amount;
      if (budget.isRecurring) {
        const period = budget.recurringPeriod || 'monthly';
        let count = 1;
        if (period === 'monthly') {
          // Count full months in range
          const startMonth = new Date(start.getFullYear(), start.getMonth(), 1);
          const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
          count = (endMonth.getFullYear() - startMonth.getFullYear()) * 12 + (endMonth.getMonth() - startMonth.getMonth()) + 1;
          // If range is only one month, just use 1
          if (count < 1) count = 1;
        } else if (period === 'weekly') {
          // Count full weeks in range
          const msPerWeek = 1000 * 60 * 60 * 24 * 7;
          count = Math.floor((end - start) / msPerWeek) + 1;
          if (count < 1) count = 1;
        } else if (period === 'yearly') {
          // Count full years in range
          count = end.getFullYear() - start.getFullYear() + 1;
          if (count < 1) count = 1;
        }
        adjustedBudget = budget.amount * count;
      }
      
      if (hasBudgetAmountChangeBy(budget, end)) adjustedBudget = getBudgetAmountInRange(budget, start, end);

      // Calculate remaining amount and percentage
      const remaining = adjustedBudget - spent;
      const percentage = adjustedBudget > 0 ? (spent / adjustedBudget) * 100 : 0;
      
      // Calculate variance (this is the key for the "vs Bud" display)
      // Variance is calculated as (actual/budget - 1) * 100
      const variance = adjustedBudget > 0 ? ((spent / adjustedBudget) - 1) * 100 : 0;
      
      return {
        budget,
        spent,
        budgetAmount: adjustedBudget,
        remaining,
        percentage,
        variance,  // Include this explicitly in the return object
        isOverBudget: percentage >= 100,
        transactions: relevantTransactions
      };
    } catch (err) {
      console.error('Error calculating budget status:', err);
      return null;
    }
  }, [transactions]);

  // Rule evaluation is synchronous; saves use bounded, category-only batches.
  // Avoid debounced promises: superseding a callback could leave a caller waiting forever.
  const applyRulesToTransactions = useCallback(
    async (ruleIds = null, options = {}) => {
      const sortedRules = (ruleIds ? rules.filter(r => ruleIds.includes(r._id)) : [...rules])
        .sort((a, b) => a.priority - b.priority);
      const txns = options.onlyUncategorized
        ? transactions.filter(t => !t.category || t.category === 'uncategorized')
        : transactions;
      const transferCategoryIds = new Set(categories.filter(c => c.isTransfer).map(c => c.id));
      const toUpdate = [];
      for (const txn of txns) {
        for (const rule of sortedRules) {
          // Transfers only take rules that file them into a Transfer-type
          // category — a merchant keyword inside a transfer reference must
          // not recategorize the transfer as spending.
          if (txn.isTransfer && !transferCategoryIds.has(rule.categoryId)) continue;

          // Function to check if a single condition matches the transaction
          const matchesCondition = (cond) => {
            const parts = cond.field.split('.');
            const fieldValue = parts.reduce((o, p) => (o != null ? o[p] : undefined), txn);
            if (fieldValue === undefined) return false;

            switch (cond.operator) {
              case 'contains':
                return String(fieldValue).toLowerCase().includes(cond.value.toLowerCase());
              case 'equals':
                return String(fieldValue).toLowerCase() === cond.value.toLowerCase();
              case 'greaterThan':
                return Number(fieldValue) > Number(cond.value);
              case 'lessThan':
                return Number(fieldValue) < Number(cond.value);
              case 'startsWith':
                return String(fieldValue).toLowerCase().startsWith(cond.value.toLowerCase());
              case 'endsWith':
                return String(fieldValue).toLowerCase().endsWith(cond.value.toLowerCase());
              case 'between':
                if (!cond.value.includes(',')) return false;
                const [min, max] = cond.value.split(',').map(Number);
                return Number(fieldValue) >= min && Number(fieldValue) <= max;
              default:
                return false;
            }
          };

          // Check if any condition group matches (OR logic between groups)
          const matchesRule = (() => {
            // If rule has condition groups, use them (new format)
            if (rule.conditionGroups && rule.conditionGroups.length > 0) {
              // Return true if ANY group matches (OR logic)
              return rule.conditionGroups.some(group => {
                // A group matches if ALL its conditions match (AND logic)
                return group.conditions && group.conditions.every(matchesCondition);
              });
            }
            // Fallback to the old format (all conditions must match - AND logic only)
            else if (rule.conditions && rule.conditions.length > 0) {
              return rule.conditions.every(matchesCondition);
            }
            
            return false; // No valid conditions to check
          })();

          if (matchesRule) {
            if (txn.category !== rule.categoryId) toUpdate.push({ _id: txn._id, category: rule.categoryId, expectedCategory: txn.category ?? null });
            break;
          }
        }
      }


      let updatedCount = 0;
      for (let offset = 0; offset < toUpdate.length; offset += 100) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 30000);
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE_URL}/transactions/categories/batch`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {})
            },
            signal: controller.signal,
            body: JSON.stringify({ updates: toUpdate.slice(offset, offset + 100) })
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data.error || 'Could not save categories.');
          const saved = new Map(data.transactions.map(t => [t._id, t.category]));
          // Merge only the changed field into current state, preserving other edits/new rows.
          setTransactions(prev => prev.map(t => saved.has(t._id) ? { ...t, category: saved.get(t._id) } : t));
          updatedCount += data.updated;
        } catch (error) {
          const reason = error.name === 'AbortError'
            ? 'The request timed out. Refresh transactions before retrying; the last batch may have saved.'
            : error.message;
          throw new Error(`Confirmed ${updatedCount} of ${toUpdate.length} category changes. ${reason}`);
        } finally {
          clearTimeout(timer);
        }
      }
      return updatedCount;
    },
    [rules, transactions, categories]
  );

  // Memoize transaction fields
  const getTransactionFields = useCallback(() => {
    const sampleTxn = transactions[0] || {
      date: '', amount: 0, description: '', account: '',
      balance: 0, category: '', notes: '', type: '',
      isTransfer: false, merchant: null, meta: {other_account: ''}
    };

    const fields = Object.keys(sampleTxn)
      .filter(f =>
        !['_id','__v','id','isTransfer'].includes(f) &&
        (sampleTxn[f] === null || typeof sampleTxn[f] !== 'object')
      )
      .map(f => ({
        name: f,
        label: f.charAt(0).toUpperCase() + f.slice(1),
        type: typeof sampleTxn[f] === 'number' ? 'number' : 'string'
      }));

    if (sampleTxn.merchant && typeof sampleTxn.merchant === 'object') {
      fields.push({ name: 'merchant.name', label: 'Merchant Name', type: 'string' });
    }

    if (sampleTxn.meta && typeof sampleTxn.meta === 'object') {
      Object.keys(sampleTxn.meta).forEach(key => {
        fields.push({
          name: `meta.${key}`,
          label: `Meta ${key.charAt(0).toUpperCase() + key.slice(1)}`,
          type: typeof sampleTxn.meta[key] === 'number' ? 'number' : 'string'
        });
      });

      if (!Object.prototype.hasOwnProperty.call(sampleTxn.meta, 'other_account')) {
        fields.push({
          name: 'meta.other_account',
          label: 'Meta Other Account',
          type: 'string'
        });
      }
    }

    return fields;
  }, [transactions]);

  // Function to anonymize (randomize) all transaction and budget data for demo mode
  const anonymizeAllData = useCallback(() => {
    function randomAmount(amount) {
      // Keep sign, randomize value within a reasonable range
      const sign = amount < 0 ? -1 : 1;
      const base = Math.max(1, Math.abs(amount));
      // Randomize within 50% to 150% of original value
      const newVal = base * (0.5 + Math.random());
      return Math.round(newVal * 100) / 100 * sign;
    }
    function randomDescription() {
      // Scooby Doo themed descriptions
      const pool = [
        'Scooby Snacks', 'Mystery Machine', 'Shaggy', 'Velma', 'Daphne', 'Fred',
        'Ruh-roh!', 'Jinkies!', 'Zoinks!', 'Jeepers!', 'Old Man Jenkins',
        'Haunted Mansion', 'Spooky Castle', 'Ghost Chase', 'Masked Villain',
        'Creepy Lighthouse', 'Swamp Monster', 'Phantom', 'Ghoul', 'Scooby-Doo',
        'Trap Set', 'Unmasking', 'Scooby-Doo Where Are You?', 'Crystal Cove',
        'Meddling Kids', 'Scooby-Doo Mystery', 'Snack Run', 'Scooby-Doo Crew',
        'Scooby-Doo Van', 'Scooby-Doo Adventure', 'Scooby-Doo Case',
        'Scooby-Doo Clue', 'Scooby-Doo Gang', 'Scooby-Doo Night',
        'Scooby-Doo Chase', 'Scooby-Doo Trap', 'Scooby-Doo Plan',
        'Scooby-Doo Escape', 'Scooby-Doo Secret', 'Scooby-Doo Riddle',
        'Scooby-Doo Hideout', 'Scooby-Doo Fright', 'Scooby-Doo Mystery Solved',
        'Scooby-Doo Snack Break', 'Scooby-Doo Ghost', 'Scooby-Doo Monster',
        'Scooby-Doo Villain', 'Scooby-Doo Mask', 'Scooby-Doo Reveal',
        'Scooby-Doo Scream', 'Scooby-Doo Adventure', 'Scooby-Doo Map',
        'Scooby-Doo Library', 'Scooby-Doo Attic', 'Scooby-Doo Basement',
        'Scooby-Doo Secret Passage', 'Scooby-Doo Hallway', 'Scooby-Doo Window',
        'Scooby-Doo Door', 'Scooby-Doo Footprints', 'Scooby-Doo Evidence',
        'Scooby-Doo Suspect', 'Scooby-Doo Clue', 'Scooby-Doo Plan',
        'Scooby-Doo Hide', 'Scooby-Doo Run', 'Scooby-Doo Snoop',
        'Scooby-Doo Snoop', 'Scooby-Doo Hideout', 'Scooby-Doo Fright',
        'Scooby-Doo Mystery Solved', 'Scooby-Doo Snack Break',
        'Scooby-Doo Ghost', 'Scooby-Doo Monster', 'Scooby-Doo Villain',
        'Scooby-Doo Mask', 'Scooby-Doo Reveal', 'Scooby-Doo Scream',
        'Scooby-Doo Adventure', 'Scooby-Doo Map', 'Scooby-Doo Library',
        'Scooby-Doo Attic', 'Scooby-Doo Basement', 'Scooby-Doo Secret Passage',
        'Scooby-Doo Hallway', 'Scooby-Doo Window', 'Scooby-Doo Door',
        'Scooby-Doo Footprints', 'Scooby-Doo Evidence', 'Scooby-Doo Suspect'
      ];
      return pool[Math.floor(Math.random() * pool.length)] + (Math.random() < 0.2 ? ' #' + Math.floor(Math.random()*1000) : '');
    }
    function randomNotes() {
      const pool = [
        'Scooby-Doo did it!', 'Let\'s split up, gang!', 'Time for Scooby Snacks!',
        'Would have gotten away with it...', 'Jinkies!', 'Zoinks!', 'Jeepers!',
        'Ruh-roh!', 'Scooby-Doo Mystery', 'Scooby-Doo solved!', 'Scooby-Doo Clue',
        'Scooby-Doo Adventure', 'Scooby-Doo Trap', 'Scooby-Doo Plan',
        'Scooby-Doo Hideout', 'Scooby-Doo Fright', 'Scooby-Doo Snack Break',
        'Scooby-Doo Ghost', 'Scooby-Doo Monster', 'Scooby-Doo Villain',
        'Scooby-Doo Mask', 'Scooby-Doo Reveal', 'Scooby-Doo Scream',
        'Scooby-Doo Map', 'Scooby-Doo Library', 'Scooby-Doo Attic',
        'Scooby-Doo Basement', 'Scooby-Doo Secret Passage', 'Scooby-Doo Hallway',
        'Scooby-Doo Window', 'Scooby-Doo Door', 'Scooby-Doo Footprints',
        'Scooby-Doo Evidence', 'Scooby-Doo Suspect', 'Scooby-Doo Hide',
        'Scooby-Doo Run', 'Scooby-Doo Snoop', 'Scooby-Doo solved the case!',
        'Scooby-Doo and the gang', 'Scooby-Doo night', 'Scooby-Doo chase',
        'Scooby-Doo unmasking', 'Scooby-Doo plan', 'Scooby-Doo snack',
        'Scooby-Doo van', 'Scooby-Doo crew', 'Scooby-Doo clue',
        'Scooby-Doo fright', 'Scooby-Doo scream', 'Scooby-Doo riddle',
        'Scooby-Doo secret', 'Scooby-Doo escape', 'Scooby-Doo hideout',
        'Scooby-Doo fright', 'Scooby-Doo solved', 'Scooby-Doo snack break'
      ];
      return pool[Math.floor(Math.random() * pool.length)];
    }
    setTransactions(prev => prev.map(txn => {
      let newAmount;
      if (txn.amount > 0) {
        // Income: set to a very large value for demo
        newAmount = 99999.99;
      } else {
        // Expense: randomize as before
        newAmount = randomAmount(txn.amount);
      }
      return {
        ...txn,
        amount: newAmount,
        description: randomDescription(),
        notes: randomNotes(),
        // Optionally randomize merchant/meta fields
        merchant: txn.merchant ? { ...txn.merchant, name: randomDescription() } : txn.merchant,
        meta: txn.meta ? { ...txn.meta, reference: undefined, particulars: undefined } : txn.meta
      };
    }));
    setBudgets(prev => prev.map(budget => {
      let newAmount;
      if (budget.type === 'Income') {
        newAmount = 99999.99;
      } else {
        newAmount = randomAmount(budget.amount);
      }
      return {
        ...budget,
        name: randomDescription(),
        amount: newAmount
      };
    }));
    setAccounts(prev => prev.map(account => {
      // Randomize balance, keep sign
      const sign = account.balance < 0 ? -1 : 1;
      const newBalance = Math.round((Math.random() * 100000) * 100) / 100 * sign;
      return {
        ...account,
        name: randomDescription(),
        balance: newBalance
      };
    }));
    setActions(prev => prev.map(action => {
      let newAmount;
      if (action.amount > 0 && action.type === 'income') {
        newAmount = 99999.99;
      } else if (typeof action.amount === 'number') {
        newAmount = randomAmount(action.amount);
      } else {
        newAmount = action.amount;
      }
      return {
        ...action,
        name: randomDescription(),
        description: randomDescription(),
        notes: randomNotes(),
        amount: newAmount
      };
    }));
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    transactions,
    categories,
    rules,
    budgets,
    accounts,
    actions,
    goals,
    isLoading,
    error,
    // Transaction functions
    addTransaction,
    updateTransaction,
    deleteTransaction,
    addMultipleTransactions,
    importTransactionsFromCSV,
    fetchTransactions,
    // Category functions
    addCategory,
    updateCategory,
    deleteCategory,
    // Rule functions
    addRule,
    updateRule,
    deleteRule,
    applyRulesToTransactions,
    getTransactionFields,
    // Budget functions
    fetchBudgets,
    addBudget,
    updateBudget,
    deleteBudget,
    deleteBudgetsByCategory,
    getBudgetStatus,
    // Account functions
    fetchAccounts,
    addAccount,
    updateAccount,
    deleteAccount,
    updateAccountBalance,
    getOrCreateAccount,
    // Action functions
    fetchActions,
    addAction,
    updateAction,
  deleteAction,
    // Goal functions
    fetchGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    // Anonymize function
    anonymizeAllData,
  }), [
    transactions,
    categories,
    rules,
    budgets,
    accounts,
    actions,
    goals,
    isLoading,
    error,
    fetchAccounts,
    addAccount,
    updateAccount,
    deleteAccount,
    updateAccountBalance,
    getOrCreateAccount,    
    addTransaction,
    updateTransaction,
    deleteTransaction,
    addMultipleTransactions,
    importTransactionsFromCSV,
    fetchTransactions,
    addCategory,
    updateCategory,
    deleteCategory,
    addRule,
    updateRule,
    deleteRule,
    applyRulesToTransactions,
    getTransactionFields,
    fetchBudgets,
    addBudget,
    updateBudget,
    deleteBudget,
    deleteBudgetsByCategory,
    getBudgetStatus,
    fetchActions,
    addAction,
    updateAction,
  deleteAction,
    fetchGoals,
    addGoal,
    updateGoal,
    deleteGoal,
    anonymizeAllData,
  ]);

  return (
    <DataContext.Provider value={contextValue}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);

